import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till, upload } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { dateTime, fmtSerial, fromMinor, money, toMinor } from "@/lib/format";
import { useScanner } from "@/lib/scanner";
import { useShop, useTill } from "@/lib/till-context";
import type { Item, Move } from "@/lib/types";
import { Badge, Button, Field, Input, Modal, Select, Thumb, cx, useToast } from "@/components/ui";
import { PageHead } from "@/components/Layout";
import { LabelsModal } from "@/components/Labels";
import { ImportProducts } from "@/components/ImportProducts";
import { Icon } from "@/components/Icon";

type WebProduct = { slug: string; variantId: string | null; name: string; sku: string | null; price: number; image: string | null; stock: number | null; type: string; category: string; brand: string; inShop: boolean; serial: boolean };
const TRACK: Record<Item["tracking"], [string, "dark" | "slate" | "amber"]> = { serial: ["IMEI / serial", "dark"], qty: ["Qty", "slate"], service: ["Service", "amber"] };

/** Imported from the website but none brought into the shop yet. */
const notInShop = (i: Item) => i.active && !!i.webSlug && i.tracking !== "service" && (i.onHand ?? 0) <= 0;

export function Products() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")) });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"" | "notin" | "low" | "nobarcode" | "shop" | "inactive">(() => (new URLSearchParams(window.location.search).get("filter") === "notin" ? "notin" : ""));
  const [open, setOpen] = useState<string | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const [sheet, setSheet] = useState(false);
  // Import from the website can be switched off in Settings.
  const tillCtx = useTill();
  const canImport = tillCtx.me?.settings.websiteImport !== false;
  // Adding, editing and importing products: owner, general manager, manager.
  const editor = ["owner", "gm", "manager"].includes(tillCtx.me?.user.role ?? "owner");
  const shop = useShop();
  const [labels, setLabels] = useState<Item[] | null>(null);
  const items = q.data?.items ?? [];
  useScanner((code) => {
    const hit = items.find((i) => i.barcode === code || i.sku === code);
    if (hit) setOpen(hit.id);
    else setSearch(code);
  }, !open && !labels && !sheet && !importing);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === "notin" && !notInShop(i)) return false;
      if (filter === "low" && !(i.tracking !== "service" && (i.onHand ?? 0) <= i.reorderAt && i.active && !notInShop(i))) return false;
      if (filter === "nobarcode" && (i.barcode || i.tracking === "service")) return false;
      if (filter === "shop" && i.webSlug) return false;
      if (filter === "inactive" ? i.active : !i.active) return false;
      return !s || i.name.toLowerCase().includes(s) || (i.sku ?? "").toLowerCase().includes(s) || (i.barcode ?? "").includes(s) || i.category.toLowerCase().includes(s);
    });
  }, [items, search, filter]);
  const count = (f: typeof filter) => items.filter((i) => (f === "notin" ? notInShop(i) : f === "low" ? i.active && i.tracking !== "service" && (i.onHand ?? 0) <= i.reorderAt && !notInShop(i) : f === "nobarcode" ? i.active && !i.barcode && i.tracking !== "service" : f === "shop" ? i.active && !i.webSlug : f === "inactive" ? !i.active : false)).length;

  const [hideTip, setHideTip] = useState(() => {
    try {
      return localStorage.getItem("pos.tip.stock") === "1";
    } catch {
      return false;
    }
  });
  const [more, setMore] = useState(false);
  const active = items.filter((i) => i.active).length;
  const stockTone = (i: Item) => {
    const oh = i.onHand ?? 0;
    return notInShop(i) ? "bg-slate-100 text-slate-700" : oh <= 0 ? "bg-stop text-white" : oh <= i.reorderAt ? "bg-warn-tint text-warn-ink" : "bg-brand-tint text-brand-ink";
  };
  const stockText = (i: Item) => (notInShop(i) ? "Not in shop" : String(i.onHand ?? 0));
  const notInCount = count("notin");
  const webLine = (i: Item) => (i.webSlug ? (i.webMissing ? "Removed from website" : `Web ${i.webStock ?? "∞"}`) : "");
  const FILTERS = [
    ["", "All", active, "text-slate-400"],
    ...(shop.website ? ([["notin", "Not in shop", notInCount, "text-warn-ink"]] as const) : []),
    ["low", "Low stock", count("low"), "text-amber-700"],
    ["nobarcode", "No barcode", count("nobarcode"), "text-slate-500"],
    ...(shop.website ? ([["shop", "Shop only", count("shop"), "text-slate-500"]] as const) : []),
    ["inactive", "Hidden", count("inactive"), "text-slate-500"],
  ] as const;
  // Open the label printer for these products (fresh from the server, with their new barcodes).
  const labelsFor = async (list: string[]) => {
    const fresh = await qc.fetchQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")), staleTime: 0 });
    const ids = new Set(list);
    setLabels(fresh.items.filter((i) => ids.has(i.id) && (i.barcode || i.sku)).slice(0, 200));
  };
  // Products with no barcode get the shop's own, then go to the label printer.
  const giveCodes = async () => {
    try {
      const r = await api<{ count: number; ids: string[] }>(till("/admin/items/codes"), { body: {} });
      toast(`${r.count} product${r.count === 1 ? "" : "s"} got a barcode`);
      await labelsFor(r.ids);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't make the barcodes", "err");
    }
  };
  const dismissTip = () => {
    setHideTip(true);
    try {
      localStorage.setItem("pos.tip.stock", "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="pb-24 sm:pb-0">
      <PageHead title="Products & shop stock" sub={q.isLoading ? "Loading…" : `${active} product${active === 1 ? "" : "s"}${shop.website ? " · counted separately from the website" : ""}`}>
        <div className="relative">
          <Button aria-label="More actions" onClick={() => setMore((m) => !m)} className="!w-11 !px-0">
            <Icon name="more" />
          </Button>
          {more ? (
            <div className="absolute right-0 top-12 z-20 w-56 border border-slate-200 bg-white py-1 shadow-lg" onMouseLeave={() => setMore(false)}>
              <button type="button" onClick={() => { setMore(false); setLabels(shown.filter((i) => i.barcode || i.sku).slice(0, 60)); }} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm hover:bg-slate-50">
                <Icon name="label" size={16} /> Print barcode labels
              </button>
              {editor ? (
                <button type="button" onClick={() => { setMore(false); setSheet(true); }} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm hover:bg-slate-50">
                  <Icon name="report" size={16} /> Import from Excel or CSV
                </button>
              ) : null}
              {editor && count("nobarcode") ? (
                <button type="button" onClick={() => { setMore(false); void giveCodes(); }} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm hover:bg-slate-50">
                  <Icon name="label" size={16} /> Give barcodes to {count("nobarcode")} without one
                </button>
              ) : null}
              <Link to="/stock" className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm hover:bg-slate-50">
                <Icon name="truck" size={16} /> Receive a delivery
              </Link>
            </div>
          ) : null}
        </div>
        {editor ? (
          <Button onClick={() => setSheet(true)} className="hidden sm:inline-flex">
            <Icon name="report" size={16} /> Import from Excel
          </Button>
        ) : null}
        {editor ? (
          <Button onClick={() => setOpen("new")} className="hidden sm:inline-flex">
            <Icon name="plus" size={16} /> Add product
          </Button>
        ) : null}
        {canImport ? (
          <Button tone="primary" onClick={() => setImporting(true)} className="hidden sm:inline-flex">
            <Icon name="cloud" size={16} /> Import from website
          </Button>
        ) : null}
      </PageHead>

      {notInCount && filter !== "notin" ? (
        <section className="mb-4 flex flex-wrap items-center gap-4 border-2 border-warn bg-warn-tint px-4 py-3.5">
          <span className="flex min-w-[260px] flex-1 flex-col gap-1">
            <b className="text-[15px]">{notInCount} imported product{notInCount === 1 ? " has" : "s have"} nothing in the shop yet</b>
            <span className="text-[13.5px] leading-relaxed text-[#4a2300]">The shop and the website keep separate stock. Importing copies the product, not its units, so these start at 0 here and the till says "Not in shop". Bring units across from the website, or receive a delivery.</span>
          </span>
          <span className="flex flex-wrap gap-2">
            <Button onClick={() => setFilter("notin")}>Show them</Button>
            <Link to="/stock?tab=transfer" className="inline-flex h-12 items-center bg-ink px-4 text-sm font-bold text-white hover:bg-graphite">Bring stock in</Link>
          </span>
        </section>
      ) : null}

      {!hideTip && items.length && shop.website ? (
        <div className="mb-4 flex items-start gap-3 border border-teal-200 bg-brand-tint px-3.5 py-2.5 text-[13px] text-brand-ink">
          <span className="flex-1">
            <b>Shop stock is its own count.</b> Till sales only change shop stock. Use <Link to="/stock?tab=transfer" className="font-semibold underline">Transfer</Link> to move units to or from the website.
          </span>
          <button type="button" aria-label="Dismiss" onClick={dismissTip} className="grid h-7 w-7 flex-none place-items-center">
            <Icon name="x" size={15} />
          </button>
        </div>
      ) : null}

      {!q.isLoading && !items.length ? (
        <section className="flex flex-col items-center gap-6 border border-slate-200 bg-white px-5 py-12 text-center">
          <span className="grid h-16 w-16 place-items-center bg-brand-tint text-brand">
            <Icon name="box" size={30} />
          </span>
          <div className="max-w-md">
            <b className="text-xl">Set up what the shop sells</b>
            <p className="mt-1.5 text-sm text-slate-500">Shop stock is counted on its own. Bring products in, then receive or transfer units so the till can sell them.</p>
          </div>
          <div className={cx("grid w-full max-w-2xl gap-3", canImport ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
            {canImport ? (
              <button type="button" onClick={() => setImporting(true)} className="flex flex-col items-start gap-2 bg-brand p-4 text-left text-white hover:bg-brand-dark">
                <Icon name="cloud" size={22} />
                <b className="text-sm">Import from website</b>
                <span className="text-xs text-teal-100">Pick products, keep prices in sync</span>
              </button>
            ) : null}
            <button type="button" onClick={() => setOpen("new")} className="flex flex-col items-start gap-2 border border-slate-200 p-4 text-left hover:border-slate-400">
              <Icon name="plus" size={22} />
              <b className="text-sm">Add a shop product</b>
              <span className="text-xs text-slate-500">For items not on the website</span>
            </button>
            <Link to="/stock" className="flex flex-col items-start gap-2 border border-slate-200 p-4 text-left hover:border-slate-400">
              <Icon name="scan" size={22} />
              <b className="text-sm">Receive a delivery</b>
              <span className="text-xs text-slate-500">Scan boxes and IMEIs into stock</span>
            </Link>
          </div>
        </section>
      ) : (
        <>
          <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center">
            <label className="flex h-11 min-w-0 flex-1 items-center gap-2 border border-slate-200 bg-white px-3 text-slate-500 focus-within:border-brand">
              <Icon name="scan" size={16} />
              <input data-scan-target value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Scan or search by name, barcode, SKU" className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none" />
            </label>
            <div className="no-scrollbar -mx-4 flex overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <div className="flex flex-none border border-slate-200 bg-white">
                {FILTERS.map(([k, l, n, tone], idx) => (
                  <button key={k} type="button" onClick={() => setFilter(k)} className={cx("h-[42px] whitespace-nowrap px-3.5 text-[13px]", idx > 0 && "border-l border-slate-200", filter === k ? "bg-slate-900 font-semibold text-white" : "hover:bg-slate-50")}>
                    {l} <span className={cx("font-mono", filter === k ? "text-slate-400" : tone)}>{n}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Wide screens: a condensed table */}
          <section className="hidden border border-slate-200 bg-white lg:block">
            <div className="grid grid-cols-[minmax(0,2.4fr)_110px_140px_120px_80px_110px_24px] gap-3 border-b border-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-500">
              <span>Product</span>
              <span>{shop.serials ? "Tracking" : "Unit"}</span>
              <span>Price</span>
              <span>Stock</span>
              <span>Reorder</span>
              <span>{shop.website ? "Source" : "Category"}</span>
              <span />
            </div>
            {shown.map((i) => (
              <button key={i.id} type="button" onClick={() => setOpen(i.id)} className="grid w-full grid-cols-[minmax(0,2.4fr)_110px_140px_120px_80px_110px_24px] items-center gap-3 border-b border-slate-50 px-4 py-3 text-left text-sm hover:bg-slate-50">
                <span className="flex min-w-0 items-center gap-3">
                  <Thumb src={i.image} name={i.name} className="h-11 w-11 flex-none" />
                  <span className="min-w-0">
                    <b className="block truncate">{i.name}</b>
                    <span className="block truncate font-mono text-[11px] text-slate-500">
                      {i.sku ?? "No SKU"} · {i.barcode ?? (i.tracking === "service" ? "fee, no barcode needed" : <span className="text-amber-700">no barcode</span>)}
                    </span>
                  </span>
                </span>
                <span>
                  {shop.serials || i.tracking === "service" ? <Badge tone={TRACK[i.tracking][1]}>{TRACK[i.tracking][0]}</Badge> : <span className="text-[13px] capitalize text-slate-600">{i.unit ?? "piece"}</span>}
                </span>
                <span className="flex flex-col">
                  <b className="font-mono">{money(i.price)}</b>
                  <span className="text-[11px] text-slate-500">{i.webPrice != null ? `Web ${money(i.webPrice)}` : i.wholesalePrice != null ? `Wholesale ${money(i.wholesalePrice)}` : shop.website ? "Shop only" : ""}</span>
                </span>
                <span className="flex flex-col items-start gap-0.5">
                  {i.tracking === "service" ? <span className="text-slate-400">n/a</span> : <span className={cx("px-2 py-0.5 font-mono font-bold", stockTone(i))}>{stockText(i)}</span>}
                  <span className="text-[11px] text-slate-500">{webLine(i)}</span>
                </span>
                <span className="font-mono text-slate-600">{i.tracking === "service" ? "n/a" : i.reorderAt}</span>
                <span className="truncate text-xs text-slate-600">{shop.website ? (i.webSlug ? (i.sync ? "Linked · synced" : "Linked") : "Shop only") : i.category || "-"}</span>
                <span className="text-slate-400">
                  <Icon name="chev" size={16} />
                </span>
              </button>
            ))}
            {!shown.length ? <p className="px-4 py-8 text-center text-sm text-slate-500">Nothing matches.</p> : null}
          </section>

          {/* Phones and tablets: cards */}
          <section className="-mx-4 border-y border-slate-200 bg-white sm:mx-0 sm:border lg:hidden">
            {shown.map((i) => (
              <button key={i.id} type="button" onClick={() => setOpen(i.id)} className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0">
                <Thumb src={i.image} name={i.name} className="h-[52px] w-[52px] flex-none" />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <b className="truncate text-sm">{i.name}</b>
                  <span className="flex items-center gap-2">
                    {shop.serials || i.tracking === "service" ? <Badge tone={TRACK[i.tracking][1]}>{TRACK[i.tracking][0]}</Badge> : <Badge tone="slate">{i.unit ?? "piece"}</Badge>}
                    <span className="font-mono text-[13px] font-bold">{money(i.price)}</span>
                  </span>
                </span>
                <span className="flex flex-none flex-col items-end gap-1">
                  {i.tracking === "service" ? <span className="text-xs text-slate-400">service</span> : <span className={cx("px-2 py-0.5 font-mono font-bold", stockTone(i))}>{stockText(i)}</span>}
                  <span className="text-[10px] text-slate-500">{i.tracking === "service" ? "" : webLine(i) || "in shop"}</span>
                </span>
              </button>
            ))}
            {!shown.length ? <p className="px-4 py-8 text-center text-sm text-slate-500">Nothing matches.</p> : null}
          </section>
        </>
      )}

      {/* Phone: actions stay in reach */}
      <div className={cx("fixed inset-x-0 bottom-0 z-20 grid gap-2 border-t border-slate-200 bg-white px-4 pb-5 pt-2.5 sm:hidden", canImport ? "grid-cols-2" : "grid-cols-1")}>
        <Button size="lg" onClick={() => setOpen("new")}>
          Add product
        </Button>
        {canImport ? (
          <Button size="lg" tone="primary" onClick={() => setImporting(true)}>
            Import
          </Button>
        ) : null}
      </div>

      {open ? (
        <ItemModal
          id={open}
          onClose={() => setOpen(null)}
          onSaved={(created) => {
            void qc.invalidateQueries({ queryKey: ["admin-items"] });
            // A new product goes straight to its label, ready to print.
            if (created?.barcode) setLabels([created]);
          }}
        />
      ) : null}
      {importing ? <ImportModal onClose={() => setImporting(false)} onDone={() => void qc.invalidateQueries({ queryKey: ["admin-items"] })} /> : null}
      {sheet ? <ImportProducts onClose={() => setSheet(false)} onImported={(ids) => void labelsFor(ids)} /> : null}
      {labels ? <LabelsModal items={labels} onClose={() => setLabels(null)} /> : null}
    </div>
  );
}

function ItemModal({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: (created?: Item) => void }) {
  const shop = useShop();
  const toast = useToast();
  const isNew = id === "new";
  const q = useQuery({ queryKey: ["admin-item", id], queryFn: () => api<{ item: Item; units: { id: string; serial: string; status: string; receivedRef: string | null; at: number }[]; moves: Move[] }>(till(`/admin/items/${id}`)), enabled: !isNew });
  const it = q.data?.item;
  const [f, setF] = useState<Record<string, string | boolean> | null>(null);
  const form = f ?? (it ? { name: it.name, image: it.image ?? "", sku: it.sku ?? "", barcode: it.barcode ?? "", category: it.category, brand: it.brand, price: fromMinor(it.price), cost: it.cost != null ? fromMinor(it.cost) : "", reorderAt: String(it.reorderAt), warrantyMonths: it.warrantyMonths != null ? String(it.warrantyMonths) : "", tracking: it.tracking, sync: it.sync, active: it.active, wholesalePrice: it.wholesalePrice != null ? fromMinor(it.wholesalePrice) : "", wholesaleMin: it.wholesaleMin != null ? String(it.wholesaleMin) : "", distributorPrice: it.distributorPrice != null ? fromMinor(it.distributorPrice) : "", distributorMin: it.distributorMin != null ? String(it.distributorMin) : "", unit: it.unit ?? "" } : isNew ? { name: "", image: "", sku: "", barcode: "", category: "", brand: "", price: "", cost: "", reorderAt: "0", warrantyMonths: "", tracking: "qty", sync: false, active: true, wholesalePrice: "", wholesaleMin: "", distributorPrice: "", distributorMin: "", unit: "" } : null);
  const [brk, setBrk] = useState<{ minQty: string; price: string }[] | null>(null);
  const breaks = brk ?? (it?.breaks ?? []).map((b) => ({ minQty: String(b.minQty), price: fromMinor(b.price) }));
  const [pk, setPk] = useState<{ name: string; qty: string }[] | null>(null);
  const packs = pk ?? (it?.packs ?? []).map((p) => ({ name: p.name, qty: String(p.qty) }));
  const set = (k: string, v: string | boolean) => setF((cur) => ({ ...(cur ?? form ?? {}), [k]: v }));
  const [adjust, setAdjust] = useState(false);
  // The units on hand when a new product is added (counted products only).
  const [opening, setOpening] = useState("");
  if (!form) return <Modal title="Loading…" onClose={onClose}><p /></Modal>;
  const synced = !!it?.webSlug && !!form.sync;
  // No barcode on the form: the shop makes one for every new product when it is saved.
  const save = async () => {
    const body = { name: form.name, ...(synced ? {} : { image: String(form.image) }), sku: form.sku, category: form.category, brand: form.brand, price: toMinor(String(form.price)), cost: form.cost === "" ? null : toMinor(String(form.cost)), reorderAt: Number(form.reorderAt) || 0, warrantyMonths: form.warrantyMonths === "" ? null : Number(form.warrantyMonths), tracking: form.tracking, sync: form.sync, active: form.active,
      wholesalePrice: form.wholesalePrice === "" ? null : toMinor(String(form.wholesalePrice)), wholesaleMin: form.wholesaleMin === "" ? null : Number(form.wholesaleMin),
      distributorPrice: form.distributorPrice === "" ? null : toMinor(String(form.distributorPrice)), distributorMin: form.distributorMin === "" ? null : Number(form.distributorMin),
      breaks: breaks.filter((b) => b.minQty && b.price).map((b) => ({ minQty: Number(b.minQty), price: toMinor(b.price) })),
      unit: String(form.unit ?? "").trim(), packs: packs.filter((p) => p.name.trim() && Number(p.qty) > 1).map((p) => ({ name: p.name.trim(), qty: Number(p.qty) })) };
    try {
      const r = await api<{ item: Item }>(till(isNew ? "/admin/items" : `/admin/items/${id}`), { method: isNew ? "POST" : "PUT", body });
      let item = r.item;
      if (isNew && item.tracking === "qty" && Number(opening) > 0) {
        try {
          await api(till("/admin/adjust"), { body: { itemId: item.id, kind: "stocktake", count: Number(opening), reason: "Opening stock" } });
          item = { ...item, onHand: Number(opening) };
        } catch (e) {
          toast(`Product added, but the stock wasn't saved: ${e instanceof Error ? e.message : "try Adjust / stocktake"}`, "err");
        }
      }
      toast(isNew ? `Product added · barcode ${item.barcode ?? ""}` : "Saved");
      onSaved(isNew || (!it?.barcode && item.barcode) ? item : undefined);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  return (
    <Modal wide title={isNew ? "Add a shop product" : String(form.name)} sub={`${it?.barcode ? `Barcode ${it.barcode} · ` : isNew ? "The barcode is made for you when you save · " : ""}${it?.webSlug ? `Linked to the website (${it.webSlug})${synced ? ". Name, photo and price follow the website" : ""}` : "Shop-only product"}`} onClose={onClose} footer={<>{!isNew && it?.tracking !== "service" ? <Button onClick={() => setAdjust(true)}>Adjust / stocktake</Button> : null}<Button tone="primary" onClick={save}>Save</Button></>}>
      <div className="flex flex-col gap-5 sm:flex-row">
      <PhotoBox value={String(form.image)} name={String(form.name)} brand={String(form.brand)} price={toMinor(String(form.price))} locked={synced} onChange={(v) => set("image", v)} />
      <div className="grid min-w-0 flex-1 content-start gap-3 sm:grid-cols-2">
        <Field label="Name" className="sm:col-span-2">
          <Input value={String(form.name)} onChange={(e) => set("name", e.target.value)} disabled={synced} />
        </Field>
        <Field label="Shop price">
          <Input inputMode="decimal" value={String(form.price)} onChange={(e) => set("price", e.target.value)} disabled={synced} className="font-mono" />
        </Field>
        <Field label="Unit cost (optional)">
          <Input inputMode="decimal" value={String(form.cost)} onChange={(e) => set("cost", e.target.value)} className="font-mono" />
        </Field>
        <Field label="Category">
          <Input value={String(form.category)} onChange={(e) => set("category", e.target.value)} />
        </Field>
        <Field label="Brand">
          <Input value={String(form.brand)} onChange={(e) => set("brand", e.target.value)} />
        </Field>
        <Field label="SKU (optional)">
          <Input value={String(form.sku)} onChange={(e) => set("sku", e.target.value)} className="font-mono" />
        </Field>
        <Field label="Sold by" hint="bag, length, sheet, kg… Blank = pieces">
          <Input list="units" value={String(form.unit ?? "")} onChange={(e) => set("unit", e.target.value)} placeholder="piece" />
          <datalist id="units">{["bag", "length", "sheet", "kg", "m", "trip", "block", "box", "roll", "bucket", "pack", "piece"].map((u) => <option key={u} value={u} />)}</datalist>
        </Field>
        <Field label="Keeping stock" hint={!isNew ? "Can only change while stock is 0" : undefined}>
          <Select value={String(form.tracking)} onChange={(e) => set("tracking", e.target.value)}>
            <option value="qty">Count how many I have</option>
            {shop.serials || form.tracking === "serial" ? <option value="serial">One by one (IMEI / serial)</option> : null}
            <option value="service">No stock (a service or fee)</option>
          </Select>
        </Field>
        {form.tracking === "service" ? null : isNew && form.tracking === "qty" ? (
          <Field label={`How many you have now${form.unit ? ` (${form.unit}s)` : ""}`} hint="The stock in the shop today. Later deliveries go in under Receive stock">
            <Input inputMode="numeric" value={opening} onChange={(e) => setOpening(e.target.value.replace(/\D/g, ""))} placeholder="0" className="font-mono" />
          </Field>
        ) : isNew ? (
          <Field label="How many you have now" hint="Scan each unit's IMEI under Receive stock after saving">
            <Input value="0" disabled className="font-mono" />
          </Field>
        ) : (
          <Field label="In stock now" hint="To change it, press Adjust / stocktake below">
            <Input value={String(it?.onHand ?? 0)} disabled className="font-mono" />
          </Field>
        )}
        {form.tracking === "service" ? null : (
          <Field label="Warn me when stock is down to" hint="It shows as Low stock at this number. 0 = no warning">
            <Input inputMode="numeric" value={String(form.reorderAt)} onChange={(e) => set("reorderAt", e.target.value.replace(/\D/g, ""))} />
          </Field>
        )}
        {shop.serials ? (
        <Field label="Warranty months" hint="Blank = the till default for this kind of product">
          <Input inputMode="numeric" value={String(form.warrantyMonths)} onChange={(e) => set("warrantyMonths", e.target.value.replace(/\D/g, ""))} />
        </Field>
        ) : null}
        <div className="flex flex-col justify-end gap-2 text-sm">
          {it?.webSlug ? (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.sync} onChange={(e) => set("sync", e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" /> Keep name, photo & price in sync with the website
            </label>
          ) : null}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" /> Sold at the till
          </label>
        </div>
      </div>
      </div>
      <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4">
        <b className="text-sm">Shop prices for wholesale and volume</b>
        {(() => {
          const cost = form.cost === "" ? null : toMinor(String(form.cost));
          const margin = (v: unknown) => {
            const p = v === "" ? null : toMinor(String(v));
            return p && cost != null ? `${(((p - cost) / p) * 100).toFixed(1)}% margin` : undefined;
          };
          return (
            <>
              <div className="grid gap-3 sm:grid-cols-4">
                <Field label="Wholesale price" hint={margin(form.wholesalePrice)}>
                  <Input inputMode="decimal" value={String(form.wholesalePrice)} onChange={(e) => set("wholesalePrice", e.target.value)} placeholder="none" className="font-mono" />
                </Field>
                <Field label="Wholesale from (units)">
                  <Input inputMode="numeric" value={String(form.wholesaleMin)} onChange={(e) => set("wholesaleMin", e.target.value.replace(/\D/g, ""))} placeholder="1" />
                </Field>
                <Field label="Distributor price" hint={margin(form.distributorPrice)}>
                  <Input inputMode="decimal" value={String(form.distributorPrice)} onChange={(e) => set("distributorPrice", e.target.value)} placeholder="none" className="font-mono" />
                </Field>
                <Field label="Distributor from (units)">
                  <Input inputMode="numeric" value={String(form.distributorMin)} onChange={(e) => set("distributorMin", e.target.value.replace(/\D/g, ""))} placeholder="1" />
                </Field>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] font-semibold">Quantity breaks (anyone buying more pays less)</span>
                {breaks.map((b, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    From <Input inputMode="numeric" aria-label="From units" value={b.minQty} onChange={(e) => setBrk(breaks.map((x, j) => (j === i ? { ...x, minQty: e.target.value.replace(/\D/g, "") } : x)))} className="!w-20" />
                    units, <Input inputMode="decimal" aria-label="Price each" value={b.price} onChange={(e) => setBrk(breaks.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} className="!w-28 font-mono" /> each
                    <span className="text-xs text-slate-500">{margin(b.price) ?? ""}</span>
                    <Button size="sm" onClick={() => setBrk(breaks.filter((_, j) => j !== i))}>Remove</Button>
                  </div>
                ))}
                {breaks.length < 6 ? <Button size="sm" className="self-start" onClick={() => setBrk([...breaks, { minQty: "", price: "" }])}>+ Add a break</Button> : null}
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] font-semibold">Bigger packs (the till can sell a pallet as 50 bags)</span>
                {packs.map((p, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    A <Input aria-label="Pack name" value={p.name} onChange={(e) => setPk(packs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="pallet" className="!w-32" />
                    is <Input inputMode="numeric" aria-label="Units in the pack" value={p.qty} onChange={(e) => setPk(packs.map((x, j) => (j === i ? { ...x, qty: e.target.value.replace(/\D/g, "") } : x)))} className="!w-20" /> {String(form.unit || "pieces")}
                    <Button size="sm" onClick={() => setPk(packs.filter((_, j) => j !== i))}>Remove</Button>
                  </div>
                ))}
                {packs.length < 4 ? <Button size="sm" className="self-start" onClick={() => setPk([...packs, { name: "", qty: "" }])}>+ Add a pack</Button> : null}
              </div>
              <p className="text-xs text-slate-500">The till charges the lowest price a buyer qualifies for: their group's price or a quantity break, never both. Prices under cost are refused. These stay shop-only.</p>
            </>
          );
        })()}
      </div>
      {it?.tracking === "serial" ? (
        <div className="mt-5">
          <b className="text-sm">Units ({q.data?.units.filter((u) => u.status === "in_stock").length ?? 0} in stock)</b>
          <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200">
            {(q.data?.units ?? []).map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 border-b border-slate-50 px-3 py-2 text-[13px]">
                <span className="font-mono font-bold">{fmtSerial(u.serial)}</span>
                <span className="text-slate-500">{u.receivedRef ?? ""}</span>
                <Badge tone={u.status === "in_stock" ? "brand" : u.status === "sold" ? "slate" : u.status === "rma" ? "red" : "amber"}>{u.status.replace("_", " ")}</Badge>
              </div>
            ))}
            {!q.data?.units.length ? <p className="p-3 text-[13px] text-slate-500">No units yet. Receive them in Receive & transfer.</p> : null}
          </div>
        </div>
      ) : null}
      {q.data?.moves.length ? (
        <div className="mt-5">
          <b className="text-sm">Recent stock changes</b>
          <div className="mt-2 max-h-48 overflow-y-auto text-[13px]">
            {q.data.moves.map((m) => (
              <div key={m.id} className="flex gap-3 border-b border-slate-50 py-1.5">
                <span className="w-28 text-slate-500">{dateTime(m.at)}</span>
                <span className="w-24 font-semibold">{m.kind.replace("_", " ")}</span>
                <span className="w-14 font-mono">{m.qty > 0 ? `+${m.qty}` : m.qty}</span>
                <span className="flex-1 truncate text-slate-500">
                  {m.serial ? fmtSerial(m.serial) : ""} {m.ref ?? ""} {m.staffName ?? ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {adjust && it ? <AdjustModal item={it} units={q.data?.units ?? []} onClose={() => setAdjust(false)} onDone={() => { void q.refetch(); onSaved(); }} /> : null}
    </Modal>
  );
}

function AdjustModal({ item, units, onClose, onDone }: { item: Item; units: { serial: string; status: string }[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState(item.tracking === "serial" ? "writeoff" : "stocktake");
  const [count, setCount] = useState(String(item.onHand ?? 0));
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [serials, setSerials] = useState<string[]>([]);
  const save = async () => {
    try {
      await api(till("/admin/adjust"), { body: { itemId: item.id, kind, count: Number(count), qty: Number(qty), reason, serials } });
      toast("Stock updated");
      onDone();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  return (
    <Modal title={`Adjust stock · ${item.name}`} sub={`Shop stock now ${item.onHand ?? 0}`} onClose={onClose} footer={<Button tone="primary" onClick={save}>Save</Button>}>
      {item.tracking === "serial" ? (
        <div className="grid gap-3">
          <Field label="What happened">
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="writeoff">Write off (lost / damaged)</option>
              <option value="rma">Faulty, back to supplier</option>
              <option value="restock">Put back into stock</option>
            </Select>
          </Field>
          <div className="max-h-56 overflow-y-auto border border-slate-200">
            {units.filter((u) => (kind === "restock" ? u.status !== "in_stock" && u.status !== "sold" : u.status === "in_stock")).map((u) => (
              <label key={u.serial} className="flex items-center gap-3 border-b border-slate-50 px-3 py-2 text-[13px]">
                <input type="checkbox" checked={serials.includes(u.serial)} onChange={(e) => setSerials(e.target.checked ? [...serials, u.serial] : serials.filter((s) => s !== u.serial))} className="h-5 w-5 accent-[var(--brand)]" />
                <span className="font-mono">{fmtSerial(u.serial)}</span>
                <span className="ml-auto text-slate-500">{u.status.replace("_", " ")}</span>
              </label>
            ))}
          </div>
          <Field label="Reason">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      ) : (
        <div className="grid gap-3">
          <Field label="What">
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="stocktake">Stocktake: set the counted number</option>
              <option value="adjust">Adjust by (+ / −)</option>
              <option value="writeoff">Write off (lost / damaged)</option>
            </Select>
          </Field>
          {kind === "stocktake" ? (
            <Field label="Counted on the shelf">
              <Input inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg" />
            </Field>
          ) : (
            <Field label={kind === "writeoff" ? "How many" : "Change (e.g. 3 or -2)"}>
              <Input value={qty} onChange={(e) => setQty(e.target.value.replace(/[^\d-]/g, ""))} className="font-mono text-lg" />
            </Field>
          )}
          <Field label="Reason">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      )}
    </Modal>
  );
}

function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const q = useQuery({ queryKey: ["web-products"], queryFn: () => api<{ products: WebProduct[] }>(till("/admin/web-products")) });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [sync, setSync] = useState(true);
  const [busy, setBusy] = useState(false);
  const key = (p: WebProduct) => `${p.slug}|${p.variantId ?? ""}`;
  const list = (q.data?.products ?? []).filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase()));
  const notIn = list.filter((p) => !p.inShop);
  const run = async () => {
    setBusy(true);
    try {
      const entries = (q.data?.products ?? []).filter((p) => sel.has(key(p))).map((p) => ({ slug: p.slug, variantId: p.variantId }));
      const r = await api<{ created: number }>(till("/admin/import"), { body: { entries, sync } });
      toast(`Imported ${r.created} product${r.created === 1 ? "" : "s"}. Receive or transfer stock to sell them`);
      onDone();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Import failed", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal wide title="Import from the online store" sub="Copies name, photo, SKU and price. Shop stock starts at 0. Phones, laptops, TVs and consoles come in tracked by IMEI / serial." onClose={onClose} footer={<><span className="mr-auto text-sm text-slate-500">{sel.size} selected</span><Button onClick={onClose}>Cancel</Button><Button tone="primary" onClick={run} disabled={!sel.size || busy}>Import {sel.size}</Button></>}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the website's products" className="min-w-[220px] flex-1" />
        <Button size="sm" onClick={() => setSel(new Set(notIn.map(key)))}>
          Select all {notIn.length}
        </Button>
        <Button size="sm" onClick={() => setSel(new Set())}>
          Clear
        </Button>
      </div>
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={sync} onChange={(e) => setSync(e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" /> Keep name, photo & price in sync with the website (stock never syncs)
      </label>
      {q.isLoading ? <p className="text-sm text-slate-500">Loading the website catalogue…</p> : null}
      <div className="flex flex-col">
        {list.map((p) => (
          <label key={key(p)} className={cx("flex items-center gap-3 border-b border-slate-100 py-2 text-sm", p.inShop && "opacity-50")}>
            <input type="checkbox" disabled={p.inShop} checked={sel.has(key(p))} onChange={(e) => { const n = new Set(sel); if (e.target.checked) n.add(key(p)); else n.delete(key(p)); setSel(n); }} className="h-5 w-5 accent-[var(--brand)]" />
            <Thumb src={p.image} name={p.name} className="h-9 w-9 flex-none" />
            <span className="min-w-0 flex-1 truncate">{p.name}</span>
            {p.serial ? <Badge tone="dark">IMEI</Badge> : null}
            <span className="w-24 text-right font-mono text-slate-500">{money(p.price)}</span>
            <span className="w-32 text-right text-xs text-slate-500">{p.inShop ? "Already in shop" : `Website stock ${p.stock ?? "∞"}`}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

/** Shrink a photo before it goes up: at most 1200px on the long side, WebP. Small files upload fast on shop data. */
async function shrink(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const k = Math.min(1, 1200 / Math.max(bmp.width, bmp.height));
  if (k === 1 && file.size < 400_000 && file.type !== "image/gif") return file;
  const cv = document.createElement("canvas");
  cv.width = Math.round(bmp.width * k);
  cv.height = Math.round(bmp.height * k);
  const ctx = cv.getContext("2d");
  if (!ctx) return file;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.drawImage(bmp, 0, 0, cv.width, cv.height);
  const out = await new Promise<Blob | null>((res) => cv.toBlob(res, "image/webp", 0.85));
  return out && out.type === "image/webp" ? out : (await new Promise<Blob | null>((res) => cv.toBlob(res, "image/jpeg", 0.85))) ?? file;
}

/**
 * The product's photo, optional. Choose a file, drop one, or take one with the tablet camera;
 * beside it, the product as it shows on the till. No photo: the till shows the name instead.
 * Locked while the product follows the website (its photo comes from there).
 */
function PhotoBox({ value, name, brand, price, locked, onChange }: { value: string; name: string; brand: string; price: number; locked: boolean; onChange: (v: string) => void }) {
  const toast = useToast();
  const pick = useRef<HTMLInputElement>(null);
  const cam = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [bad, setBad] = useState(false);
  const send = async (file: File | undefined) => {
    if (!file || locked) return;
    if (!file.type.startsWith("image/")) return toast("Choose a photo (JPG, PNG or WebP)", "err");
    setBusy(true);
    try {
      const blob = await shrink(file);
      const fd = new FormData();
      fd.append("file", blob, blob.type === "image/webp" ? "photo.webp" : blob.type === "image/jpeg" ? "photo.jpg" : file.name);
      const r = await upload<{ url: string }>(till("/admin/items/photo"), fd);
      setBad(false);
      onChange(r.url);
      toast("Photo added. Save to keep it");
    } catch (e) {
      toast(e instanceof Error ? e.message : "The photo didn't upload", "err");
    } finally {
      setBusy(false);
    }
  };
  const has = !!value && !bad;
  return (
    <div className="flex flex-none flex-col gap-2 sm:w-56">
      <span className="text-[13px] font-semibold">
        Photo <span className="font-normal text-muted">(optional)</span>
      </span>
      <button
        type="button"
        disabled={locked || busy}
        onClick={() => pick.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!locked) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void send(e.dataTransfer.files[0]);
        }}
        aria-label={has ? "Change the photo" : "Add a photo"}
        className={cx("relative grid aspect-[4/3] w-full place-items-center overflow-hidden bg-white text-center", has ? "border border-line" : "border-2 border-dashed border-line-2", over && "border-brand bg-brand-tint", !locked && "hover:border-ink")}
      >
        {has ? (
          <img src={mediaUrl(value, 600)} alt="" onError={() => setBad(true)} className="h-full w-full object-contain p-2" />
        ) : (
          <span className="flex flex-col gap-1 px-3">
            <b className="text-[13px]">{locked ? "No photo on the website" : "Add a photo"}</b>
            {locked ? null : <span className="text-[11px] text-muted">Drop an image or tap to choose. JPG, PNG or WebP, up to 5 MB</span>}
          </span>
        )}
        {busy ? <span className="absolute inset-0 grid place-items-center bg-white/80 text-[13px] font-semibold">Uploading…</span> : null}
      </button>
      <input ref={pick} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { void send(e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void send(e.target.files?.[0]); e.target.value = ""; }} />
      {locked ? (
        <span className="text-xs text-muted">Follows the website. Turn off sync below to use your own.</span>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" disabled={busy} onClick={() => cam.current?.click()}>
            Take photo
          </Button>
          <Button size="sm" className="flex-1" disabled={busy || !value} onClick={() => { setBad(false); onChange(""); }}>
            Remove
          </Button>
        </div>
      )}
      <div className="flex items-start gap-2.5">
        <div className="w-24 flex-none border border-line bg-white">
          <span className="grid aspect-[4/3] w-full place-items-center overflow-hidden border-b border-[#ebe9e3]">
            {has ? <img src={mediaUrl(value, 240)} alt="" className="h-full w-full object-contain p-1" /> : <span className="px-1.5 text-center text-[9.5px] font-bold leading-tight text-muted">{(name || "Product name").split(" ").slice(0, 4).join(" ")}</span>}
          </span>
          <span className="flex flex-col px-1.5 py-1">
            {brand ? <span className="truncate text-[8.5px] font-bold uppercase tracking-wider text-muted">{brand}</span> : null}
            <span className="line-clamp-2 text-[10px] font-semibold leading-tight">{name || "Product name"}</span>
            <span className="font-mono text-[10px] font-semibold">{money(price)}</span>
          </span>
        </div>
        <span className="text-[11px] leading-snug text-muted">How it shows on the till.{has ? "" : " Without a photo the till shows the name."}</span>
      </div>
    </div>
  );
}
